import type { CharacterLook } from '../game/types';
import type { MoveDef, MoveId, OpponentDef, SideLook } from './types';

/* Tuning for the between-shift fights. Frame data is in seconds, distances in stage pixels. */

export const STAGE = {
  width: 256,
  height: 144,
  left: 20,
  right: 236,
  floorY: 128,
  /** Fighters can't stand closer than this (centre to centre). */
  minGap: 20,
  /** Hurtbox half-width. */
  bodyHalf: 8,
} as const;

export const BOUT = {
  time: 40,
  introSeconds: 2.2,
  koSeconds: 2.8,
  parryWindow: 0.16,
  blockChip: 0.15,
  comboWindow: 0.95,
  inputBuffer: 0.16,
  knockbackDecay: 9,
  playerCool: 100,
  playerWalk: { forward: 74, back: 58 },
  superCost: 100,
  superGain: { hit: 9, hurt: 6, parry: 25, chairCatch: 45 },
  hitstop: { light: 0.05, heavy: 0.1, parry: 0.14, super: 0.4 },
  /** Nine lingo words, then the ALL THE WAY finisher. */
  superHits: 10,
  superHitEvery: 0.08,
  superDamage: 4,
} as const;

/** Every hit in a combo calls out the next piece of hash brown lingo. */
export const LINGO_CALLOUTS = [
  'SCATTERED!',
  'SMOTHERED!',
  'COVERED!',
  'CHUNKED!',
  'DICED!',
  'PEPPERED!',
  'CAPPED!',
  'TOPPED!',
  'COUNTRY!',
] as const;

const move = (def: MoveDef): MoveDef => def;

export const MOVES: Record<MoveId, MoveDef> = {
  // ── the cook ──
  jab: move({
    id: 'jab',
    startup: 0.07,
    active: 0.08,
    recovery: 0.14,
    reach: 26,
    damage: 4,
    knockback: 8,
    hitstun: 0.24,
    chain: 'jab2',
    pose: 'jab',
  }),
  jab2: move({
    id: 'jab2',
    startup: 0.07,
    active: 0.08,
    recovery: 0.14,
    reach: 26,
    damage: 4,
    knockback: 8,
    hitstun: 0.24,
    chain: 'smack',
    pose: 'jab',
  }),
  smack: move({
    id: 'smack',
    startup: 0.12,
    active: 0.1,
    recovery: 0.38,
    reach: 30,
    damage: 8,
    knockback: 30,
    hitstun: 0.42,
    heavy: true,
    pose: 'smack',
  }),
  toss: move({
    id: 'toss',
    startup: 0.2,
    active: 0.05,
    recovery: 0.26,
    reach: 0,
    damage: 0,
    knockback: 0,
    hitstun: 0,
    cooldown: 0.85,
    projectile: { kind: 'hashBrown', speed: 150, damage: 7, hitstun: 0.32, knockback: 10 },
    pose: 'throw',
  }),
  super: move({
    id: 'super',
    startup: 0.22,
    active: 0.42,
    recovery: 0.5,
    reach: 24,
    damage: 5,
    knockback: 36,
    hitstun: 0.6,
    heavy: true,
    dash: { speed: 230 },
    pose: 'super',
  }),
  // ── rowdy customers ──
  shove: move({
    id: 'shove',
    startup: 0.22,
    active: 0.1,
    recovery: 0.34,
    reach: 26,
    damage: 10,
    knockback: 16,
    hitstun: 0.36,
    pose: 'jab',
  }),
  slap: move({
    id: 'slap',
    startup: 0.15,
    active: 0.08,
    recovery: 0.2,
    reach: 24,
    damage: 6,
    knockback: 6,
    hitstun: 0.26,
    chain: 'slap2',
    pose: 'jab',
  }),
  slap2: move({
    id: 'slap2',
    startup: 0.11,
    active: 0.08,
    recovery: 0.3,
    reach: 24,
    damage: 8,
    knockback: 12,
    hitstun: 0.3,
    pose: 'smack',
  }),
  haymaker: move({
    id: 'haymaker',
    startup: 0.42,
    active: 0.12,
    recovery: 0.5,
    reach: 31,
    damage: 18,
    knockback: 30,
    hitstun: 0.5,
    heavy: true,
    armor: true,
    pose: 'smack',
  }),
  charge: move({
    id: 'charge',
    startup: 0.36,
    active: 0.5,
    recovery: 0.55,
    reach: 20,
    damage: 14,
    knockback: 26,
    hitstun: 0.45,
    heavy: true,
    armor: true,
    dash: { speed: 190 },
    pose: 'charge',
  }),
  throw: move({
    id: 'throw',
    startup: 0.42,
    active: 0.05,
    recovery: 0.42,
    reach: 0,
    damage: 0,
    knockback: 0,
    hitstun: 0,
    cooldown: 1.2,
    pose: 'throw',
  }),
  taunt: move({
    id: 'taunt',
    startup: 0,
    active: 0,
    recovery: 1.15,
    reach: 0,
    damage: 0,
    knockback: 0,
    hitstun: 0,
    cooldown: 3,
    pose: 'taunt',
  }),
};

function look(parts: Partial<CharacterLook>): CharacterLook {
  return {
    species: 'human',
    skin: '#eab28a',
    hair: '#3d2718',
    hairStyle: 'short',
    hat: 'none',
    hatColor: '#c0392b',
    eyes: 'normal',
    facialHair: 'none',
    shirt: '#2e86de',
    shirtAlt: '#ffffff',
    shirtStyle: 'plain',
    accessory: 'none',
    ...parts,
  };
}

/** You. Visor, yellow uniform shirt, apron, spatula. */
export const COOK: SideLook = {
  cook: true,
  legs: 'slacks',
  shoes: 'sneakers',
  look: look({
    skin: '#c68a5e',
    hair: '#24170f',
    hairStyle: 'short',
    hat: 'visor',
    hatColor: '#1e1e24',
    shirt: '#f4c20d',
    shirtAlt: '#1e1e24',
    shirtStyle: 'polo',
  }),
};

export const ROSTER: readonly OpponentDef[] = [
  {
    id: 'jorts',
    name: 'GUY IN JORTS',
    tier: 1,
    side: {
      legs: 'jorts',
      shoes: 'sneakers',
      look: look({
        skin: '#eab28a',
        hair: '#6b4125',
        hat: 'trucker',
        hatColor: '#c0392b',
        facialHair: 'stubble',
        shirt: '#f2f2f2',
        shirtStyle: 'tank',
      }),
    },
    rage: 110,
    walkSpeed: 50,
    weight: 1,
    ai: {
      aggression: 0.55,
      reaction: 0.5,
      blockChance: 0.1,
      projectileBlock: 0.1,
      guard: 0.35,
      reversal: 0.35,
      think: [0.35, 0.65],
      preferredRange: 30,
      moves: [
        { move: 'shove', weight: 5, minRange: 0, maxRange: 30 },
        { move: 'charge', weight: 2, minRange: 42, maxRange: 120 },
        { move: 'taunt', weight: 1, minRange: 45, maxRange: 400 },
      ],
      tauntChance: 0.12,
    },
    lines: {
      intro: 'YOU WANNA GO, SPATULA?',
      taunts: ['COME ON THEN!', "I'LL SIT WHEN I WANNA SIT!"],
      calm: '...my bad, chief. Scattered, smothered, please.',
      win: "THAT'S WHAT I THOUGHT.",
    },
  },
  {
    id: 'randy',
    name: 'UNCLE RANDY',
    tier: 1,
    side: {
      legs: 'slacks',
      shoes: 'sneakers',
      look: look({
        skin: '#f6d3b3',
        hair: '#8f8f8f',
        hairStyle: 'buzz',
        hat: 'cap',
        hatColor: '#2e7d32',
        facialHair: 'mustache',
        eyes: 'glasses',
        shirt: '#8e2b2b',
        shirtAlt: '#3d1414',
        shirtStyle: 'flannel',
      }),
    },
    rage: 115,
    walkSpeed: 46,
    weight: 1.1,
    ai: {
      aggression: 0.58,
      reaction: 0.5,
      blockChance: 0.15,
      projectileBlock: 0.1,
      guard: 0.3,
      reversal: 0.35,
      think: [0.38, 0.7],
      preferredRange: 28,
      moves: [
        { move: 'shove', weight: 4, minRange: 0, maxRange: 30 },
        { move: 'haymaker', weight: 3, minRange: 0, maxRange: 34 },
        { move: 'taunt', weight: 2, minRange: 40, maxRange: 400 },
      ],
      tauntChance: 0.2,
    },
    lines: {
      intro: "I DIDN'T START IT!",
      taunts: ["I'M NOT EVEN MAD!", 'YOU KNOW WHO I AM?'],
      calm: "...you're right. I'll sit. Coffee?",
      win: 'RANDY WINS AGAIN!',
    },
  },
  {
    id: 'bridesmaid',
    name: 'BRIDESMAID #3',
    tier: 2,
    side: {
      legs: 'skirt',
      shoes: 'heels',
      look: look({
        skin: '#d49a6a',
        hair: '#e2c275',
        hairStyle: 'long',
        hat: 'tiara',
        shirt: '#fd79a8',
        shirtStyle: 'sparkle',
        accessory: 'sash',
      }),
    },
    rage: 130,
    walkSpeed: 64,
    weight: 0.9,
    projectile: { kind: 'bouquet', speed: 115, damage: 10, hitstun: 0.3, knockback: 10 },
    ai: {
      aggression: 0.66,
      reaction: 0.36,
      blockChance: 0.2,
      projectileBlock: 0.25,
      guard: 0.5,
      reversal: 0.5,
      think: [0.28, 0.5],
      preferredRange: 34,
      moves: [
        { move: 'slap', weight: 5, minRange: 0, maxRange: 27 },
        { move: 'throw', weight: 3, minRange: 50, maxRange: 170 },
        { move: 'charge', weight: 1, minRange: 40, maxRange: 110 },
        { move: 'taunt', weight: 1, minRange: 60, maxRange: 400 },
      ],
      tauntChance: 0.1,
    },
    lines: {
      intro: "IT'S HER WEDDING WEEKEND!!!",
      taunts: ['WOOOOO!', 'BRIDE TRIBE!!!'],
      calm: "...can we get a table for eight? We'll be good.",
      win: "SHE'S GETTING MARRIED!!!",
    },
  },
  {
    id: 'karen',
    name: 'KAREN FROM BOOTH 2',
    tier: 2,
    side: {
      legs: 'slacks',
      shoes: 'slides',
      look: look({
        skin: '#f6d3b3',
        hair: '#e2c275',
        hairStyle: 'bun',
        eyes: 'sunglasses',
        shirt: '#74b9ff',
        shirtStyle: 'plain',
      }),
    },
    rage: 130,
    walkSpeed: 52,
    weight: 1,
    projectile: { kind: 'yell', speed: 112, damage: 12, hitstun: 0.36, knockback: 14 },
    ai: {
      aggression: 0.7,
      reaction: 0.4,
      blockChance: 0.25,
      projectileBlock: 0.2,
      guard: 0.5,
      reversal: 0.45,
      think: [0.26, 0.48],
      preferredRange: 62,
      moves: [
        { move: 'throw', weight: 5, minRange: 45, maxRange: 180 },
        { move: 'shove', weight: 3, minRange: 0, maxRange: 30 },
        { move: 'charge', weight: 2, minRange: 42, maxRange: 120 },
        { move: 'taunt', weight: 1, minRange: 60, maxRange: 400 },
      ],
      tauntChance: 0.12,
    },
    lines: {
      intro: 'I WANT TO SPEAK TO YOUR MANAGER.',
      taunts: ['UNACCEPTABLE!', "I'M FILMING THIS!"],
      calm: "...fine. I'll have the waffle. It's actually good.",
      win: "I'll be leaving a review.",
    },
  },
  {
    id: 'gymbro',
    name: 'GYM BRO',
    tier: 2,
    side: {
      legs: 'shorts',
      shoes: 'sneakers',
      look: look({
        skin: '#b77b4f',
        hair: '#24170f',
        hairStyle: 'buzz',
        hat: 'headband',
        hatColor: '#e74c3c',
        shirt: '#2d3436',
        shirtStyle: 'tank',
      }),
    },
    rage: 140,
    walkSpeed: 56,
    weight: 1.5,
    projectile: { kind: 'shaker', speed: 140, damage: 11, hitstun: 0.3, knockback: 12 },
    ai: {
      aggression: 0.62,
      reaction: 0.32,
      blockChance: 0.35,
      projectileBlock: 0.35,
      guard: 0.55,
      reversal: 0.5,
      think: [0.3, 0.55],
      preferredRange: 30,
      moves: [
        { move: 'haymaker', weight: 4, minRange: 0, maxRange: 33 },
        { move: 'shove', weight: 3, minRange: 0, maxRange: 30 },
        { move: 'charge', weight: 2, minRange: 42, maxRange: 120 },
        { move: 'throw', weight: 1, minRange: 60, maxRange: 170 },
        { move: 'taunt', weight: 1, minRange: 50, maxRange: 400 },
      ],
      tauntChance: 0.1,
    },
    lines: {
      intro: 'YOU EVEN LIFT, BRO?',
      taunts: ['LIGHT WEIGHT!', 'THIS IS MY CARDIO!'],
      calm: 'Bro... good workout. Protein waffle?',
      win: 'GAINS.',
    },
  },
  {
    id: 'floridaMan',
    name: 'FLORIDA MAN',
    tier: 3,
    side: {
      legs: 'jorts',
      shoes: 'barefoot',
      look: look({
        skin: '#f2a07b',
        hair: '#a86b35',
        hairStyle: 'mullet',
        eyes: 'sunglasses',
        facialHair: 'stubble',
        shirt: '#1abc9c',
        shirtAlt: '#f9e79f',
        shirtStyle: 'hawaiian',
      }),
    },
    rage: 165,
    walkSpeed: 60,
    weight: 1.2,
    projectile: { kind: 'chair', speed: 128, damage: 16, hitstun: 0.5, knockback: 24 },
    ai: {
      aggression: 0.74,
      reaction: 0.3,
      blockChance: 0.3,
      projectileBlock: 0.3,
      guard: 0.6,
      reversal: 0.62,
      think: [0.22, 0.42],
      preferredRange: 36,
      moves: [
        { move: 'shove', weight: 3, minRange: 0, maxRange: 30 },
        { move: 'haymaker', weight: 3, minRange: 0, maxRange: 33 },
        { move: 'charge', weight: 3, minRange: 42, maxRange: 130 },
        { move: 'throw', weight: 2, minRange: 60, maxRange: 180 },
        { move: 'taunt', weight: 1, minRange: 70, maxRange: 400 },
      ],
      tauntChance: 0.1,
    },
    lines: {
      intro: "I'VE WRESTLED GATORS, SON.",
      taunts: ["THE GATOR'S IN THE TRUCK!", 'FLORIDAAAA!'],
      calm: "...you know what? You're alright. All the way, please.",
      win: 'FLORIDAAAAA!',
    },
  },
  {
    id: 'dj',
    name: "DJ LIL' HASHBROWN",
    tier: 3,
    side: {
      legs: 'sweats',
      shoes: 'sneakers',
      look: look({
        skin: '#8d5a36',
        hair: '#e05fb0',
        hairStyle: 'afro',
        eyes: 'sunglasses',
        shirt: '#1e1e24',
        shirtStyle: 'sparkle',
        accessory: 'chain',
      }),
    },
    rage: 155,
    walkSpeed: 66,
    weight: 1,
    projectile: { kind: 'record', speed: 150, damage: 12, hitstun: 0.3, knockback: 12 },
    ai: {
      aggression: 0.72,
      reaction: 0.28,
      blockChance: 0.3,
      projectileBlock: 0.35,
      guard: 0.6,
      reversal: 0.58,
      think: [0.22, 0.42],
      preferredRange: 46,
      moves: [
        { move: 'slap', weight: 3, minRange: 0, maxRange: 27 },
        { move: 'throw', weight: 4, minRange: 45, maxRange: 180 },
        { move: 'charge', weight: 2, minRange: 42, maxRange: 120 },
        { move: 'haymaker', weight: 1, minRange: 0, maxRange: 33 },
        { move: 'taunt', weight: 1, minRange: 70, maxRange: 400 },
      ],
      tauntChance: 0.1,
    },
    lines: {
      intro: 'THE AFTER PARTY IS HERE NOW.',
      taunts: ['DROP THE BASS!', 'IS THIS SONG ON THE JUKEBOX?!'],
      calm: '...okay, this jukebox actually slaps. Pecan waffle?',
      win: 'AND THE CROWD GOES WILD!',
    },
  },
];

export const DOTTIE_FIGHT_LINES = {
  intro: [
    'Baby, use your words. ...Or your spatula.',
    "Day shift's late. Calm 'em down before you clock in.",
    "They ain't leaving 'til somebody calms 'em down. That's you.",
  ],
  parry: ['Did you just— with a SPATULA?', 'Blocked it like a bad Yelp review.'],
  calm: [
    "That's how we do it at the House.",
    'See? Everybody wants a waffle eventually.',
    'Another one sittin’ down. Good.',
  ],
  lose: ['Go take five, sugar.', 'Breathe, baby. Breathe.'],
} as const;
